import { useMemo } from 'react';
import { AreaDto, BlockDto, ISeatReservationDto, PlanDto, RowSettingsDto } from '../../../clients/services';
import { PlanAreaSelection } from '../../Tickets/plan/Plan';
import { IBlockDisplayInfo } from '../../Tickets/services/plan-info';
import { PlanFactory } from '../../Tickets/services/plan-factory';
import { RestrictionChecker } from '../../Tickets/services/restriction-checker';

type PlanHistoryEntry = {
    plan: PlanDto;
    summary: string;
};

const EMPTY_RESERVATIONS: ISeatReservationDto[] = [];

export const getDuplicateBlockIds = (plan: PlanDto | null): number[] => {
    if (!plan) {
        return [];
    }

    const idCounts = new Map<number, number>();
    (plan.blocks ?? []).forEach((block) => {
        idCounts.set(block.id, (idCounts.get(block.id) ?? 0) + 1);
    });

    return Array.from(idCounts.entries())
        .filter(([, count]) => count > 1)
        .map(([id]) => id)
        .sort((a, b) => a - b);
};

export const usePlanEditorDerived = ({
    planIdParam,
    history,
    historyIndex,
    selectedBlockId,
    selectedArea,
    selectedRowNr,
}: {
    planIdParam: string | undefined;
    history: PlanHistoryEntry[];
    historyIndex: number;
    selectedBlockId: number | null;
    selectedArea: PlanAreaSelection | null;
    selectedRowNr: number | null;
}) => {
    const routePlanId = useMemo(() => {
        const parsed = planIdParam ? Number.parseInt(planIdParam, 10) : NaN;
        return Number.isInteger(parsed) ? parsed : null;
    }, [planIdParam]);

    const currentEntry = historyIndex >= 0 ? history[historyIndex] : undefined;
    const draft = currentEntry?.plan;

    const canUndo = historyIndex > 0;
    const canRedo = historyIndex >= 0 && historyIndex < history.length - 1;

    const blockInfos: IBlockDisplayInfo[] = useMemo(() => {
        if (!draft) {
            return [];
        }
        const factory = new PlanFactory(new RestrictionChecker(draft.restrictions), EMPTY_RESERVATIONS, draft);
        return factory.getBlocks();
    }, [draft]);

    const selectedBlock = useMemo(() => {
        if (!draft || selectedBlockId === null) {
            return undefined;
        }
        return draft.blocks.find((block: BlockDto) => block.id === selectedBlockId);
    }, [draft, selectedBlockId]);

    const selectedAreaDto = useMemo((): AreaDto | undefined => {
        if (!draft || !selectedArea) {
            return undefined;
        }

        if (selectedArea.type === 'stage') {
            return draft.stage;
        }
        if (selectedArea.type === 'mixer') {
            return draft.mixer;
        }
        if (selectedArea.type === 'entrance') {
            return draft.entrances?.[selectedArea.index];
        }
        return draft.corridors?.[selectedArea.index];
    }, [draft, selectedArea]);

    const selectedRowSettings = useMemo((): RowSettingsDto | undefined => {
        if (!selectedBlock || selectedRowNr === null) {
            return undefined;
        }
        return selectedBlock.rowSettings?.find((rowSetting) => rowSetting.row === selectedRowNr);
    }, [selectedBlock, selectedRowNr]);

    const selectedBlockRestriction = useMemo(() => {
        if (!draft || selectedBlockId === null) {
            return undefined;
        }
        return draft.restrictions?.blockRestrictions?.find((restriction) => restriction.blockId === selectedBlockId);
    }, [draft, selectedBlockId]);

    const selectedRowRestriction = useMemo(() => {
        if (!selectedBlockRestriction || selectedRowNr === null) {
            return undefined;
        }
        return selectedBlockRestriction.rowRestrictions?.find((restriction) => restriction.row === selectedRowNr);
    }, [selectedBlockRestriction, selectedRowNr]);

    const duplicateBlockIds = useMemo(() => getDuplicateBlockIds(draft ?? null), [draft]);
    const hasDuplicateBlockIds = duplicateBlockIds.length > 0;

    const selectableRows = useMemo(() => {
        if (!selectedBlock || !selectedBlock.numberOfRows || selectedBlock.numberOfRows <= 0) {
            return [] as number[];
        }
        const start = selectedBlock.startRowsAt ?? 1;
        return Array.from({ length: selectedBlock.numberOfRows }, (_, index) => start + index);
    }, [selectedBlock]);

    return {
        routePlanId,
        currentEntry,
        draft,
        canUndo,
        canRedo,
        blockInfos,
        selectedBlock,
        selectedAreaDto,
        selectedRowSettings,
        selectedBlockRestriction,
        selectedRowRestriction,
        duplicateBlockIds,
        hasDuplicateBlockIds,
        selectableRows,
    };
};
